'use client';

import { Card } from '@components/card';
import { Typography } from '@components/typography';
import { classNames } from '@lib/classNames/classNames';
import { memo, useCallback, useRef, useState } from 'react';

import { AccordionItemProps, AccordionProps } from './types';

import IconArrow from '@/assets/icons/iconBaselinePlayArrow.svg';

export const Accordion: React.FC<AccordionProps> = memo(
  ({ items, className = '' }) => {
    const [activeItemIndex, setActiveItemIndex] = useState<number | null>(null);

    const toggleActiveItem = useCallback((index: number) => {
      setActiveItemIndex((current) => (current === index ? null : index));
    }, []);

    return (
      <div
        className={classNames({
          cls: 'flex flex-col gap-4',
          additional: [className],
        })}
      >
        {items.map((item, index) => (
          <AccordionItem
            key={index}
            item={item}
            index={index}
            isActive={activeItemIndex === index}
            onHeaderClick={toggleActiveItem}
          />
        ))}
      </div>
    );
  },
);

const AccordionItem: React.FC<AccordionItemProps> = memo(
  ({ item, isActive, index, onHeaderClick }) => {
    const contentRef = useRef<HTMLDivElement>(null);

    return (
      <Card className="px-7 py-8 max-sm:px-6 max-sm:py-5">
        <button
          type="button"
          className={
            'text-text-base-primary flex w-full cursor-pointer items-center justify-between max-sm:items-start'
          }
          onClick={() => onHeaderClick(index)}
          aria-label={item.title}
        >
          <p
            id={item.id}
            className="text-left text-[18px] leading-[28px] font-medium tracking-[-0.18px] max-sm:text-[20px] max-sm:leading-[24px] max-sm:tracking-[-0.1px]"
          >
            {item.title}
          </p>

          <IconArrow
            className={classNames({
              cls: 'ml-8 h-[24px] min-h-[24px] w-[24px] min-w-[24px] transform transition-transform duration-300',
              mods: { 'rotate-[-180deg]': isActive },
            })}
          />
        </button>

        <div
          className="transition-height-in-out relative top-[20px] overflow-hidden duration-300 max-sm:top-[12px]"
          ref={contentRef}
          style={{
            height: isActive ? contentRef.current?.scrollHeight : 0,
          }}
        >
          <Typography
            id={item.contentId}
            color="secondary"
            className="pb-3 max-sm:pb-2"
            as="div"
          >
            {item.content}
          </Typography>
        </div>
      </Card>
    );
  },
);
